import type { EditorDoc, MediaAspect, MediaEntry, MediaKind, Vec3 } from '@roomtreatment/core';
import {
  PROJECTOR_BODY, faceToward, mediaDeviceSize, mediaPictureSize,
} from '@roomtreatment/core';
import {
  clampPose, poseToTransform, roomSurfaces, surfaceKey, type SurfaceFrame,
} from './panelSurface';
import { mediaSurfaceOptions } from './mediaSurface';

/** Centre height of a wall-mounted TV, metres — roughly seated eye level,
 *  which is where a TV actually goes. Clamped into the wall if the picture is
 *  too tall to sit there. */
const TV_CENTRE_HEIGHT_M = 1.15;

/** Centre height of a projection screen, metres. Higher than a TV: a screen
 *  is usually hung above the equipment it sits behind. */
const SCREEN_CENTRE_HEIGHT_M = 1.5;

/** How far below the ceiling a projector hangs by default, metres. */
const DEFAULT_DROP_M = 0.25;

/** How far back from the screen wall a projector sits, as a fraction of the
 *  room's depth. Behind the seat, which is where a ceiling mount goes. */
const PROJECTOR_THROW_FRACTION = 0.62;

function clampNum(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/**
 * A TV placed on `frame`, centred and at a sensible height.
 *
 * Routed through `clampPose` rather than straight to a transform, so it
 * inherits window avoidance and corner clearance for free — a default that
 * lands across a window would be the first thing a user has to undo.
 * Nudged along the wall to clear any media already on it, the same 8-step
 * search `defaultWindow` uses.
 */
export function defaultTvOn(
  frame: SurfaceFrame,
  doc: EditorDoc,
  diagonalM: number,
  aspect: MediaAspect,
): Omit<MediaEntry, 'id'> {
  const size = mediaDeviceSize('tv', { diagonalM, aspect });
  const v = clampNum(TV_CENTRE_HEIGHT_M, size.h / 2, Math.max(size.h / 2, frame.vMax - size.h / 2));
  const onThisWall = (doc.media ?? []).filter(
    (m) => !m.detached && m.kind === 'tv' && sameWall(m, frame, doc),
  );

  const step = size.w + 0.3;
  let best: { position: Vec3; rotation: Vec3 } | null = null;
  for (let k = 0; k < 8 && !best; k += 1) {
    for (const dir of k === 0 ? [0] : [1, -1]) {
      const pose = clampPose(frame, { u: frame.uMax / 2 + dir * k * step, v, spin: 0 }, size, doc);
      const t = poseToTransform(frame, pose, size);
      const clear = onThisWall.every(
        (m) => Math.abs(m.position[0] - t.position[0]) + Math.abs(m.position[2] - t.position[2])
          > (mediaDeviceSize(m.kind, m).w + size.w) / 2,
      );
      if (clear) { best = t; break; }
    }
  }
  const seat = best ?? poseToTransform(
    frame, clampPose(frame, { u: frame.uMax / 2, v, spin: 0 }, size, doc), size,
  );

  return {
    kind: 'tv',
    label: null,
    diagonalM,
    aspect,
    position: seat.position,
    rotation: seat.rotation,
  };
}

/** Which wall a projector should throw at by default: the one the listener
 *  faces if `roomSurfaces` could work that out, else the first wall. */
export function defaultScreenWall(surfaces: SurfaceFrame[]): SurfaceFrame | null {
  const walls = mediaSurfaceOptions('tv', surfaces);
  return walls.find((w) => w.label.startsWith('Front')) ?? walls[0] ?? null;
}

/**
 * A projector hung from the ceiling, aimed at a screen on the front wall.
 *
 * It arrives WITH its screen: a projector throwing at nothing is not a setup
 * anyone wants, and the screen is the acoustically significant half of the
 * pair (a 120" screen is a large hard reflector; the projector is a 33 cm box).
 *
 * Unlike a TV, the stored transform is the BODY's, and its rotation is a yaw
 * toward the screen centre rather than the ceiling's own frame — a projector
 * throws horizontally, so the mounting surface's normal is the wrong facing
 * entirely. See mediaHostSurface for the consequences of getting this wrong.
 */
export function defaultProjector(
  surfaces: SurfaceFrame[],
  doc: EditorDoc,
  diagonalM: number,
  aspect: MediaAspect,
): Omit<MediaEntry, 'id'> | null {
  const ceiling = surfaces.find((s) => s.ref.kind === 'ceiling');
  const wall = defaultScreenWall(surfaces);
  if (!ceiling || !wall) return null;

  const picture = mediaPictureSize('projector', { diagonalM, aspect });
  const screenV = clampNum(
    SCREEN_CENTRE_HEIGHT_M,
    picture.h / 2,
    Math.max(picture.h / 2, wall.vMax - picture.h / 2),
  );
  const screenU = clampNum(
    wall.uMax / 2, picture.w / 2, Math.max(picture.w / 2, wall.uMax - picture.w / 2),
  );

  // Screen centre in world space, so the projector can be aimed at it.
  const screenCentre = wall.origin.clone()
    .addScaledVector(wall.u, screenU)
    .addScaledVector(wall.v, screenV);

  // Back from the screen wall along its inward normal, on the wall's centre
  // line, hanging below the ceiling.
  const throwDepth = Math.max(1.2, roomDepthAlong(wall, doc) * PROJECTOR_THROW_FRACTION);
  const bodyY = Math.max(
    PROJECTOR_BODY.h / 2,
    ceilingHeightOf(ceiling) - DEFAULT_DROP_M - PROJECTOR_BODY.h / 2,
  );
  const onFloor = wall.origin.clone()
    .addScaledVector(wall.u, screenU)
    .addScaledVector(wall.n, throwDepth);
  const position: Vec3 = [onFloor.x, bodyY, onFloor.z];

  return {
    kind: 'projector',
    label: null,
    diagonalM,
    aspect,
    position,
    rotation: [0, faceToward(position, [screenCentre.x, screenCentre.y, screenCentre.z]), 0],
    standoff: DEFAULT_DROP_M,
    screen: {
      surface: surfaceKey(wall.ref),
      u: screenU,
      v: screenV,
    },
  };
}

/**
 * A new display of `kind` where a display belongs: a TV centred on the wall
 * the seat faces, a projector hung from the ceiling toward a screen on that
 * wall. The one default for every way a display arrives (the Media rail, a
 * catalogue model, a room seeded from a gear page, a display moved out of
 * gear), so they all land in the same place. Null when the room has no wall,
 * or no ceiling for a projector.
 */
export function defaultDisplay(
  doc: EditorDoc,
  kind: MediaKind,
  diagonalM: number,
  aspect: MediaAspect,
): Omit<MediaEntry, 'id'> | null {
  const surfaces = roomSurfaces(doc);
  if (kind === 'projector') return defaultProjector(surfaces, doc, diagonalM, aspect);
  const wall = defaultScreenWall(surfaces);
  return wall ? defaultTvOn(wall, doc, diagonalM, aspect) : null;
}

/** How deep the room runs away from `wall`, metres. */
function roomDepthAlong(wall: SurfaceFrame, doc: EditorDoc): number {
  const { Lx, Ly } = doc.dims;
  // The wall's normal is axis-aligned for a rectangular room; for a polygon
  // this is still the right order of magnitude, and the caller floors it.
  return Math.abs(wall.n.x) > Math.abs(wall.n.z) ? Lx : Ly;
}

function ceilingHeightOf(ceiling: SurfaceFrame): number {
  return ceiling.origin.y;
}

/** Is this media object mounted on `frame`? Compared by surface key rather
 *  than by re-inferring, which would be circular during placement. */
function sameWall(m: MediaEntry, frame: SurfaceFrame, doc: EditorDoc): boolean {
  void doc;
  // Distance from the wall plane: anything seated on it is within a panel's
  // depth of it.
  const d = Math.abs(
    (m.position[0] - frame.origin.x) * frame.n.x
    + (m.position[1] - frame.origin.y) * frame.n.y
    + (m.position[2] - frame.origin.z) * frame.n.z,
  );
  return d < 0.5;
}

export { TV_CENTRE_HEIGHT_M, SCREEN_CENTRE_HEIGHT_M, DEFAULT_DROP_M };
